
import * as React from "react"
import * as SliderPrimitive from "@radix-ui/react-slider"
import { cn } from "@/lib/utils"
import { useThemeMode } from "@/hooks/useThemeMode"
import { useThemeContext, getGlowClasses } from "@/hooks/useThemeContext"

const EnhancedSlider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { isDark } = useThemeMode();
  const { useGradients, useGlow } = useThemeContext();
  const [isDragging, setIsDragging] = React.useState(false);

  // Calculate the completion percentage for each thumb
  const values = props.value || props.defaultValue || [];
  const min = props.min || 0;
  const max = props.max || 100;
  const percentages = values.map(value => ((value - min) / (max - min)) * 100);

  return (
    <SliderPrimitive.Root
      ref={ref}
      className={cn(
        "relative flex w-full touch-none select-none items-center",
        className
      )}
      onPointerDown={() => setIsDragging(true)}
      onPointerUp={() => setIsDragging(false)}
      {...props}
    >
      <SliderPrimitive.Track 
        className={cn(
          "relative h-2 w-full grow overflow-hidden rounded-full",
          isDark ? "bg-background/30" : "bg-muted/70"
        )}
      >
        <SliderPrimitive.Range 
          className={cn(
            "absolute h-full transition-all",
            useGradients 
              ? "bg-gradient-to-r from-primary via-primary/90 to-primary/80" 
              : "bg-primary",
            isDragging && "duration-0"
          )} 
        />
        
        {/* Subtle animated glow effect inside the track */}
        {useGlow && (
          <div 
            className="absolute h-full bg-primary/20 animate-pulse"
            style={{
              width: `${Math.max(...percentages)}%`,
              filter: "blur(4px)",
              opacity: 0.7
            }}
          />
        )}
      </SliderPrimitive.Track>
      
      {values.map((_, i) => (
        <SliderPrimitive.Thumb
          key={i}
          className={cn(
            "block h-5 w-5 rounded-full border-2 transition-all duration-150",
            isDark 
              ? "border-primary bg-background" 
              : "border-primary bg-background",
            isDragging && "scale-110",
            useGlow ? getGlowClasses(true, "primary") : "",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "disabled:pointer-events-none disabled:opacity-50"
          )}
        />
      ))}
    </SliderPrimitive.Root>
  );
});

EnhancedSlider.displayName = SliderPrimitive.Root.displayName;

export { EnhancedSlider };
